import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { z } from 'zod';
import { CheckCircle2, XCircle, LogIn, UserPlus } from 'lucide-react';
import { APP_CONFIG } from '@/config/app';
import { clearPendingOAuthLogin, recordLogin } from '@/lib/loginTracking';
import { useAuth } from '@/hooks/useAuth';
import { getSessionUser } from '@/integrations/supabase/session';
import { AuthDivider, GoogleButton } from '@/components/auth/GoogleButton';
import { useGoogleSignInEnabled } from '@/hooks/useGoogleSignInEnabled';
import {
  clearOAuthInvite,
  clearOAuthParamsFromUrl,
  describeOAuthError,
  markOAuthInvite,
  peekOAuthInvite,
  readOAuthError,
} from '@/lib/oauthReturn';

const passwordSchema = z.object({
  fullName: z.string().min(2, 'Name must be at least 2 characters'),
  password: z.string().min(6, 'Password must be at least 6 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

const loginSchema = z.object({
  password: z.string().min(1, 'Password is required'),
});

interface InviteValidationResult {
  valid: boolean;
  invite_id: string | null;
  email: string | null;
  role: 'admin' | 'csr' | 'affiliate' | 'tech' | null;
  company_id: string | null;
  company_name: string | null;
  error_message: string | null;
  user_exists: boolean;
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Administrator',
  csr: 'Customer Service Rep',
  affiliate: 'Affiliate',
  tech: 'Technician',
};

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isValidating, setIsValidating] = useState(true);
  const [inviteValid, setInviteValid] = useState(false);
  const [inviteData, setInviteData] = useState<InviteValidationResult | null>(null);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [isExistingUser, setIsExistingUser] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const { signInWithGoogle } = useAuth();
  const googleEnabled = useGoogleSignInEnabled();

  useEffect(() => {
    // Clear state on mount to prevent stale data
    setInviteValid(false);
    setInviteData(null);
    setAccepted(false);
    setIsExistingUser(false);

    void start();
  }, [token]);

  const start = async () => {
    const returningFromGoogle = !!token && peekOAuthInvite('member') === token;
    const oauthError = readOAuthError();
    if (returningFromGoogle || oauthError) {
      clearOAuthInvite();
      clearOAuthParamsFromUrl();
    }

    if (oauthError) {
      clearPendingOAuthLogin();
      toast({
        title: 'Could not continue with Google',
        // No ticket for that address: a different Google account than the invited email.
        description: describeOAuthError(
          oauthError,
          'That Google account does not match this invitation. Choose the Google account for the email address the invitation was sent to.',
        ),
        variant: 'destructive',
      });
    } else if (returningFromGoogle && (await finishGoogleReturn())) {
      return;
    }

    await validateInvite();
  };

  // Back from Google with a session. Either handle_new_user already accepted
  // the invite while creating the account, or Google signed in an existing
  // user who still has to be moved to the inviting company. Returns false to
  // fall back to the normal invite screen.
  const finishGoogleReturn = async (): Promise<boolean> => {
    const user = await getSessionUser();
    if (!user) return false;

    const { data, error } = await supabase.rpc('validate_invite_token', { p_token: token });
    if (error) return false;
    const invite = (Array.isArray(data) ? data[0] : data) as InviteValidationResult | undefined;

    if (!invite?.valid) {
      // Consumed by the account Google just created.
      navigate('/', { replace: true });
      return true;
    }

    if ((user.email ?? '').toLowerCase() !== (invite.email ?? '').toLowerCase()) {
      await supabase.auth.signOut();
      toast({
        title: 'Wrong Google account',
        description: `You signed in as ${user.email}, but this invitation is for ${invite.email}. Choose that Google account.`,
        variant: 'destructive',
      });
      return false;
    }

    const { data: acceptResult, error: acceptError } = await supabase.rpc(
      'accept_invite_for_existing_user',
      { p_token: token }
    );
    const result = acceptResult as { success: boolean; error?: string; company_name?: string; message?: string } | null;
    if (acceptError || !result?.success) {
      await supabase.auth.signOut();
      toast({
        title: 'Error',
        description: acceptError?.message || result?.error || 'Failed to accept invitation',
        variant: 'destructive',
      });
      return false;
    }

    setInviteData(invite);
    setIsExistingUser(true);
    setInviteValid(true);
    setAccepted(true);
    setIsValidating(false);
    toast({
      title: 'Success!',
      description: result.message || `You have been moved to ${result.company_name}. Redirecting...`,
    });

    // Sign out so JWT claims refresh on next login
    await supabase.auth.signOut();
    setTimeout(() => {
      navigate('/auth');
    }, 3000);
    return true;
  };

  const handleGoogle = async () => {
    if (!token || !inviteData?.email) return;
    setIsGoogleLoading(true);

    try {
      // Lets handle_new_user accept the Google account for the invited email.
      const { data: booked, error: bookError } = await supabase.rpc('book_oauth_invite_ticket', {
        p_token: token,
        p_kind: 'member',
      });
      if (bookError) throw bookError;
      if (!booked) throw new Error('This invitation has expired or is no longer valid.');

      markOAuthInvite('member', token);
      const { error } = await signInWithGoogle({
        returnPath: `/invite/accept/${encodeURIComponent(token)}`,
        loginHint: inviteData.email,
      });
      if (error) throw error;
      // On success the browser is already leaving for Google.
    } catch (err) {
      clearOAuthInvite();
      clearPendingOAuthLogin();
      setIsGoogleLoading(false);
      toast({
        title: 'Could not continue with Google',
        description: (err as { message?: string })?.message || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const validateInvite = async () => {
    if (!token) {
      console.error('❌ No token provided in URL');
      setInviteValid(false);
      setIsValidating(false);
      toast({
        title: "No Token",
        description: "No invitation token found in URL",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log('🔍 Validating invite token (first 20 chars):', token.substring(0, 20) + '...');
      
      const { data, error } = await supabase.rpc('validate_invite_token', { 
        p_token: token 
      });

      console.log('📡 RPC Response:', {
        raw_data: data,
        data_type: typeof data,
        is_array: Array.isArray(data),
        array_length: Array.isArray(data) ? data.length : 'N/A',
        error: error
      });

      if (error) {
        console.error('❌ RPC call failed:', error);
        toast({
          title: "Validation Error",
          description: error.message,
          variant: "destructive",
        });
        setInviteValid(false);
        setIsValidating(false);
        return;
      }

      if (!data) {
        console.error('❌ No data returned from validation');
        toast({
          title: "Invalid Response",
          description: "No response from server",
          variant: "destructive",
        });
        setInviteValid(false);
        setIsValidating(false);
        return;
      }

      // The function returns an array with one result
      const result = (Array.isArray(data) ? data[0] : data) as InviteValidationResult;
      
      console.log('🔍 Extracted validation result:', {
        result_object: result,
        valid: result?.valid,
        error_message: result?.error_message,
        email: result?.email,
        role: result?.role,
        company_id: result?.company_id,
        company_name: result?.company_name,
        invite_id: result?.invite_id,
        user_exists: result?.user_exists
      });
      
      if (!result) {
        console.error('❌ Could not extract result from response');
        toast({
          title: "Invalid Response",
          description: "Failed to parse validation response",
          variant: "destructive",
        });
        setInviteValid(false);
      } else if (result.valid !== true) {
        const errorMsg = result.error_message || 'This invitation link is invalid or has expired';
        console.warn('⚠️ Invite is INVALID:', errorMsg);
        toast({
          title: "Invalid Invitation",
          description: errorMsg,
          variant: "destructive",
        });
        setInviteValid(false);
      } else {
        console.log('✅ Invite is VALID! Data:', {
          email: result.email,
          role: result.role,
          company_id: result.company_id,
          company_name: result.company_name,
          user_exists: result.user_exists
        });
        setInviteValid(true);
        setInviteData(result);
        setIsExistingUser(result.user_exists);
        
        if (result.user_exists) {
          toast({
            title: "Account Found",
            description: "You already have an account. Please sign in to accept this invite.",
          });
        } else {
          toast({
            title: "Valid Invitation",
            description: "Invitation verified successfully!",
          });
        }
      }
    } catch (err) {
      console.error('❌ Unexpected error during validation:', err);
      toast({
        title: "Error",
        description: "An unexpected error occurred while validating the invitation",
        variant: "destructive",
      });
      setInviteValid(false);
    } finally {
      setIsValidating(false);
    }
  };

  // Handler for NEW users - create account
  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const validated = passwordSchema.parse({ fullName, password, confirmPassword });
      
      console.log('🔐 Creating account for:', inviteData?.email);
      console.log('🏢 Company ID:', inviteData?.company_id);
      console.log('👤 Role:', inviteData?.role);
      
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: inviteData?.email || '',
        password: validated.password,
        options: {
          emailRedirectTo: `${APP_CONFIG.appUrl}/`,
          data: {
            full_name: validated.fullName,
            invite_token: token, // REQUIRED by handle_new_user() trigger
          },
        },
      });

      if (authError) {
        console.error('❌ Signup failed:', authError);
        
        // Check if user already exists
        if (authError.message.includes('already registered') || authError.message.includes('already been registered')) {
          setIsExistingUser(true);
          toast({
            title: "Account Already Exists",
            description: "An account with this email already exists. Please sign in instead.",
            variant: "destructive",
          });
          return;
        }
        
        // Check if the DB trigger rejected due to invalid invite
        if (authError.message.includes('Account creation requires a valid invitation') ||
            authError.message.includes('Invalid or expired invitation') ||
            authError.message.includes('email does not match')) {
          toast({
            title: "Invitation Invalid",
            description: "This invitation has expired or is no longer valid. Please request a new invitation from your administrator.",
            variant: "destructive",
          });
          return;
        }
        
        throw authError;
      }

      if (!authData.user) {
        throw new Error('No user data returned from signup');
      }

      console.log('✅ Account created successfully! User ID:', authData.user.id);
      
      setAccepted(true);
      
      toast({
        title: 'Success!',
        description: 'Your account has been created. Redirecting to login...',
      });
      
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
    } catch (error) {
      console.error('❌ Error during acceptance:', error);
      
      if (error instanceof z.ZodError) {
        toast({
          title: 'Validation error',
          description: error.errors[0].message,
          variant: 'destructive',
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Failed to create account';
        toast({
          title: 'Error creating account',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Handler for EXISTING users - sign in and link to new company
  const handleSignInAndAccept = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      loginSchema.parse({ password });
      
      console.log('🔐 Signing in existing user:', inviteData?.email);
      
      // First, sign in
      const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
        email: inviteData?.email || '',
        password: password,
      });

      if (authError) {
        console.error('❌ Sign in failed:', authError);
        throw authError;
      }

      if (!authData.user) {
        throw new Error('No user data returned from sign in');
      }

      console.log('✅ Signed in successfully! User ID:', authData.user.id);
      void recordLogin(authData.user);

      // Now call the RPC to accept the invite and move user to new company
      const { data: acceptResult, error: acceptError } = await supabase.rpc(
        'accept_invite_for_existing_user',
        { p_token: token }
      );

      if (acceptError) {
        console.error('❌ Failed to accept invite:', acceptError);
        throw acceptError;
      }

      const result = acceptResult as { success: boolean; error?: string; company_name?: string; message?: string };
      
      if (!result.success) {
        throw new Error(result.error || 'Failed to accept invitation');
      }

      console.log('✅ Invite accepted! Result:', result);
      
      setAccepted(true);
      
      toast({
        title: 'Success!',
        description: result.message || `You have been moved to ${result.company_name}. Redirecting...`,
      });
      
      // Sign out so JWT claims refresh on next login
      await supabase.auth.signOut();
      
      setTimeout(() => {
        navigate('/auth');
      }, 3000);
    } catch (error) {
      console.error('❌ Error during sign in and accept:', error);
      
      if (error instanceof z.ZodError) {
        toast({
          title: 'Validation error',
          description: error.errors[0].message,
          variant: 'destructive',
        });
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Failed to sign in';
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (isValidating) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-lg">Validating invitation...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!inviteValid) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <XCircle className="h-16 w-16 text-destructive" />
            </div>
            <CardTitle className="text-center">Invalid Invitation</CardTitle>
            <CardDescription className="text-center">
              This invitation link is invalid, expired, or has already been used.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button className="w-full" onClick={() => navigate('/auth')}>
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (accepted) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-4">
              <CheckCircle2 className="h-16 w-16 text-success" />
            </div>
            <CardTitle className="text-center">
              {isExistingUser ? 'Company Updated!' : 'Account Created!'}
            </CardTitle>
            <CardDescription className="text-center">
              {inviteData?.role === 'tech'
                ? `Your technician login for ${inviteData?.company_name} is ready. Open the Steddy app on your phone and sign in with this email.`
                : isExistingUser
                  ? `You have been moved to ${inviteData?.company_name}. Redirecting to sign in...`
                  : 'Your account has been successfully created. Redirecting to sign in...'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // EXISTING USER FLOW - Show login form
  if (isExistingUser) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <div className="flex items-center justify-center mb-2">
              <LogIn className="h-10 w-10 text-primary" />
            </div>
            <CardTitle className="text-center">Account Already Exists</CardTitle>
            <CardDescription className="text-center">
              You already have an account with <strong>{inviteData?.email}</strong>.
              <br />
              Sign in to accept your invitation to <strong>{inviteData?.company_name}</strong> as <strong>{ROLE_LABELS[inviteData?.role ?? ''] ?? inviteData?.role}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {googleEnabled && (
              <>
                <GoogleButton
                  label="Sign in with Google & accept"
                  onClick={handleGoogle}
                  isLoading={isGoogleLoading}
                  disabled={isLoading}
                />
                <AuthDivider className="my-6" label="or use your password" />
              </>
            )}
            <form onSubmit={handleSignInAndAccept} className="space-y-4">
              <div className="space-y-2">
                <Label>Email</Label>
                <Input value={inviteData?.email || ''} disabled />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="Enter your existing password"
                />
              </div>

              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Signing in...' : 'Sign In & Accept Invite'}
              </Button>
              
              <div className="text-center text-sm text-muted-foreground">
                <button 
                  type="button"
                  className="underline hover:text-foreground"
                  onClick={() => navigate('/forgot-password')}
                >
                  Forgot your password?
                </button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // NEW USER FLOW - Show signup form
  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-background to-secondary/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="flex items-center justify-center mb-2">
            <UserPlus className="h-10 w-10 text-primary" />
          </div>
          <CardTitle className="text-center">Accept Invitation</CardTitle>
          <CardDescription className="text-center">
            You've been invited to join <strong>{inviteData?.company_name}</strong> as <strong>{ROLE_LABELS[inviteData?.role ?? ''] ?? inviteData?.role}</strong>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {googleEnabled && (
            <>
              <GoogleButton onClick={handleGoogle} isLoading={isGoogleLoading} disabled={isLoading} />
              <AuthDivider className="my-6" label="or create a password" />
            </>
          )}
          <form onSubmit={handleCreateAccount} className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={inviteData?.email || ''} disabled />
            </div>

            <div className="space-y-2">
              <Label htmlFor="fullName">Full Name</Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                required
                placeholder="John Doe"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                placeholder="••••••••"
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Creating account...' : 'Accept & Create Account'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
